import { useState } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import SquareIcon from '@mui/icons-material/Square';

import Card from '../../../components/Card';
import Loading from '../../../components/Loading';
import ECharts from '../../../components/ECharts';
import Switch from '../../../components/Switch';
import useWindowDimensions from '../../../hooks/useWindowDimensions';
import { formatDisplayNumber } from '../../../utils/format';
import { parseDate } from '../../../utils/date';
import { getMinMax, generateMinMaxChartData } from '../../../utils/helpers';
import { TC_COLORS, HOME_CHART_COLOR } from '../../../constants/colors';
import * as CardStyles from '../../../components/Card/Card.styles';

import {
  transferHistoryCapChartData,
  getDayAgoPercent,
  getDayAgoPercentForChartTooltip,
  getCapInterval,
  getIndexNameByStock,
  getIndexTooltipForTradingChart,
} from '../Dashboard.helpers';
import * as Styles from '../Dashboard.styles';

const TradingVolumeSmallCap = ({
  isLoading,
  historyCap,
  isDarkMode,
  tradingVolumeChartType,
  setTradingVolumeChartType,
  tradingVolumeExchangeHistoryChartData,
  selectedStock,
}) => {
  const { width } = useWindowDimensions();
  const data = transferHistoryCapChartData(
    historyCap?.chartData,
    tradingVolumeChartType === 'percent' ? 'percentTradingVolumeCap' : 'KLGD',
    'totalTradingVolumeCap',
    'KLGD',
    tradingVolumeExchangeHistoryChartData,
    selectedStock
  );

  const getChartOption = (
    xAxisData,
    seriesData,
    capMinMax,
    series2Data,
    selectedStock
  ) => {
    const bgColor = isDarkMode ? '#1a1c23' : '#fff';
    const color = isDarkMode ? '#e5e7eb' : '#24262d';
    const closePriceMinMax = getMinMax(series2Data);
    const minMaxAxisRight = generateMinMaxChartData(
      closePriceMinMax[0],
      closePriceMinMax[1],
      5,
      100
    );
    return {
      backgroundColor: bgColor,
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
        },
        position: function (point, params, dom, rect, size) {
          if (width > 1024) {
            return [
              point[0] - size.contentSize[0],
              point[1] - size.contentSize[1],
            ];
          }
          if (point[0] + point[0] / 2 > size.viewSize[0]) {
            return [
              point[0] - size.contentSize[0],
              point[1] - size.contentSize[1],
            ];
          }
          return [point[0] / 2, point[1] - size.contentSize[1]];
        },
        formatter: (params) => {
          const item = historyCap.chartData.find(
            (e) => parseDate(e.DTYYYYMMDD) === params[0].axisValue
          );
          const item2 = tradingVolumeExchangeHistoryChartData.find(
            (e) => parseDate(e.DTYYYYMMDD) === params[0].axisValue
          );
          const dayAgo = data.newData[params[0].dataIndex];
          return `
            <div>
              <div class="font-bold">${params[0].axisValue}</div>
              <div class="">
                <div>
                  <span>KLGD:</span>
                  <span class="ml-1 inline-flex font-bold">
                    ${formatDisplayNumber(Number(item.KLGD))} cp
                  </span>
                  ${getDayAgoPercentForChartTooltip(
                    Number(item.KLGD),
                    Number(dayAgo.total)
                  )}
                </div>
                <div>
                  <span>Khớp lệnh:</span>
                  <span class="ml-1 inline-flex font-bold">
                    ${formatDisplayNumber(Number(item.KLGDkhoplenh))} cp
                  </span>
                </div>
                <div>
                  <span>Thỏa thuận:</span>
                  <span class="ml-1 inline-flex font-bold">
                    ${formatDisplayNumber(Number(item.KLGDthoathuan))} cp
                  </span>
                </div>
                <div>
                  <span>Thống kê:</span>
                  <span class="ml-1 inline-flex">
                    <span
                      class="flex items-center leading-none mx-1"
                      style="color: ${TC_COLORS.GREEN}"
                    >
                      <svg class="w-3.5 h-3.5" focusable="false" aria-hidden="true" viewBox="0 0 24 24" data-testid="ArrowUpwardIcon"><path d="m4 12 1.41 1.41L11 7.83V20h2V7.83l5.58 5.59L20 12l-8-8-8 8z" style="fill: ${
                        TC_COLORS.GREEN
                      }"></path></svg>
                      ${item.tanggia}
                    </span>
                    <span
                      class="flex items-center leading-none mx-1"
                      style="color: ${TC_COLORS.PURPLE}"
                    >
                      ${item.tangtran}
                    </span>
                    <span
                      class="flex items-center leading-none mx-1"
                      style="color: ${TC_COLORS.YELLOW}"
                    >
                      <svg class="w-3.5 h-3.5" focusable="false" aria-hidden="true" viewBox="0 0 24 24" data-testid="SquareIcon" style="fill: ${
                        TC_COLORS.YELLOW
                      }"><path d="M3 3h18v18H3z"></path></svg>
                      ${item.dunggia}
                    </span>
                    <span
                      class="flex items-center leading-none mx-1"
                      style="color: ${TC_COLORS.RED}"
                    >
                      <svg class="w-3.5 h-3.5" focusable="false" aria-hidden="true" viewBox="0 0 24 24" data-testid="ArrowDownwardIcon" style="fill: ${
                        TC_COLORS.RED
                      }"><path d="m20 12-1.41-1.41L13 16.17V4h-2v12.17l-5.58-5.59L4 12l8 8 8-8z"></path></svg>
                      ${item.giamgia}
                    </span>
                    <span
                      class="flex items-center leading-none mx-1"
                      style="color: ${TC_COLORS.BLUE}"
                    >
                      ${item.giamsan}
                    </span>
                  </span>
                </div>
                ${
                  item2
                    ? `<div>
                        <span>${getIndexNameByStock(selectedStock)}:</span>
                        <span class="ml-1 inline-flex">
                          ${getIndexTooltipForTradingChart(
                            item2,
                            selectedStock
                          )}
                        </span>
                      </div>`
                    : ''
                }
              </div>
            </div>
          `;
        },
      },
      color: [
        isDarkMode ? HOME_CHART_COLOR.dark : HOME_CHART_COLOR.light,
        isDarkMode ? HOME_CHART_COLOR.white : HOME_CHART_COLOR.black,
      ],
      grid: {
        left: 0,
        right: 0,
        top: 10,
        bottom: 30,
      },
      xAxis: [
        {
          type: 'category',
          axisTick: {
            show: false,
          },
          data: xAxisData,
          axisLabel: {
            color,
            showMinLabel: true,
            showMaxLabel: false,
            interval: getCapInterval(xAxisData, width),
            align: 'left',
          },
          name: xAxisData[xAxisData.length - 1],
          nameLocation: 'end',
          nameGap: 0,
          nameTextStyle: {
            color,
            align: 'right',
            verticalAlign: 'top',
            padding: [8, 0, 0, 0],
          },
        },
      ],
      yAxis: [
        {
          type: 'value',
          max: tradingVolumeChartType === 'percent' ? 100 : capMinMax[1],
          axisLabel: {
            show: false,
          },
          splitLine: {
            show: false,
          },
          axisLine: {
            show: false,
          },
          axisTick: {
            show: false,
          },
        },
        {
          type: 'value',
          axisLabel: {
            show: false,
          },
          splitLine: {
            show: false,
          },
          axisLine: {
            show: false,
          },
          axisTick: {
            show: false,
          },
          showSymbol: false,
          min: minMaxAxisRight.min,
          max: minMaxAxisRight.max,
          interval: (minMaxAxisRight.max - minMaxAxisRight.min) / 5,
        },
      ],
      series: [
        {
          name: 'KLGD',
          type: 'bar',
          emphasis: {
            focus: 'series',
          },
          data: seriesData,
          barMaxWidth: 40,
          backgroundStyle: {
            color: 'rgba(180, 180, 180, 0.2)',
          },
        },
        {
          data: series2Data,
          type: 'line',
          name: 'Giá đóng cửa',
          yAxisIndex: 1,
          showSymbol: false,
        },
      ],
    };
  };

  const getTitle = () => {
    return (
      <div className="flex justify-between items-center">
        <CardStyles.CardTitle>Small Cap</CardStyles.CardTitle>
        <div className="inline-flex">
          <Switch
            defaultChecked={tradingVolumeChartType === 'percent'}
            onChange={(checked) =>
              setTradingVolumeChartType(checked ? 'percent' : 'value')
            }
          />{' '}
          %
        </div>
      </div>
    );
  };

  return (
    <Card customTitle={getTitle()}>
      {isLoading ? <Styles.EmptyTag /> : null}
      <Loading isLoading={isLoading} className="white" />
      {!isLoading ? (
        <>
          {!data ? (
            <Box>Không có dữ liệu</Box>
          ) : (
            <Box className="text-sm">
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box className="flex items-center">
                    <span>KLGD:</span>
                    <span className="ml-1 inline-flex">
                      {formatDisplayNumber(historyCap.historyCap.KLGD)} cp
                    </span>
                    {getDayAgoPercent(
                      historyCap.historyCap.KLGD,
                      historyCap.dayAgo.KLGD
                    )}
                  </Box>
                  <Box className="mt-1">
                    <span>Khớp lệnh:</span>
                    <span className="ml-1 inline-flex">
                      {formatDisplayNumber(historyCap.historyCap.KLGDkhoplenh)}{' '}
                      cp
                    </span>
                  </Box>
                  <Box className="mt-1">
                    <span>Thỏa thuận:</span>
                    <span className="ml-1 inline-flex">
                      {formatDisplayNumber(historyCap.historyCap.KLGDthoathuan)}{' '}
                      cp
                    </span>
                  </Box>
                  <Box className="mt-1">
                    <span>Thống kê:</span>
                    <Styles.Statistics className="ml-1 statistics">
                      <span
                        className="statistics-item"
                        style={{ color: TC_COLORS.GREEN }}
                      >
                        <ArrowUpwardIcon />
                        {historyCap.historyCap.tanggia}
                      </span>
                      <span
                        className="statistics-item"
                        style={{ color: TC_COLORS.PURPLE }}
                      >
                        {historyCap.historyCap.tangtran}
                      </span>
                      <span
                        className="statistics-item"
                        style={{ color: TC_COLORS.YELLOW }}
                      >
                        <SquareIcon />
                        {historyCap.historyCap.dunggia}
                      </span>
                      <span
                        className="statistics-item"
                        style={{ color: TC_COLORS.RED }}
                      >
                        <ArrowDownwardIcon />
                        {historyCap.historyCap.giamgia}
                      </span>
                      <span
                        className="statistics-item"
                        style={{ color: TC_COLORS.BLUE }}
                      >
                        {historyCap.historyCap.giamsan}
                      </span>
                    </Styles.Statistics>
                  </Box>
                </Grid>
              </Grid>
              <Box>
                <ECharts
                  options={getChartOption(
                    data.xAxisData,
                    data.seriesData,
                    data.capMinMax,
                    data.series2Data,
                    selectedStock
                  )}
                  height="250px"
                />
              </Box>
            </Box>
          )}
        </>
      ) : null}
    </Card>
  );
};

export default TradingVolumeSmallCap;
